import React, { FC } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  BoardFilters,
  HubFacet,
  LayerToggles,
  SortKey,
  StatusKey,
  activeFilterCount,
  isHiddenByDefaultClient,
} from "map/liveBoard";
import { BOARD_COLORS, LEG_COLOR } from "./palette";
import LegGlyph from "./LegGlyph";
import MultiSelectFilter, { MultiSelectOption } from "./MultiSelectFilter";

const STATUS_CHIPS: { key: StatusKey; label: string; glyph: string; title: string }[] = [
  { key: "to_pickup", label: "To pickup", glyph: "to_pickup", title: "Begin Trip logged, not yet at the pickup" },
  { key: "to_dropoff", label: "To dropoff", glyph: "to_dropoff", title: "At or past the pickup; next stop is the dropoff" },
  { key: "return", label: "Returning", glyph: "return", title: "Crew dropped; the van is heading back to its hub" },
  { key: "pending_ok", label: "Pending · assigned", glyph: "pending", title: "Not started; driver and vehicle assigned" },
  { key: "pending_nodriver", label: "Pending · no driver", glyph: "pending", title: "Not started; no driver assigned yet" },
  { key: "pending_novehicle", label: "Pending · no vehicle", glyph: "pending", title: "Not started; no vehicle assigned yet" },
];

const HOURS = [2, 4, 6, 8, 12, 24];

const SORTS: { value: SortKey; label: string }[] = [
  { value: "pickup", label: "Sort: pickup time" },
  { value: "event", label: "Sort: latest event" },
  { value: "gps", label: "Sort: stalest GPS" },
  { value: "hub", label: "Sort: hub" },
];

const Chip: FC<{
  pressed: boolean;
  onClick: () => void;
  title?: string;
  color?: string;
  children: React.ReactNode;
}> = ({ pressed, onClick, title, color, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-pressed={pressed}
    title={title}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      px: 0.75,
      py: 0.4,
      borderRadius: 1,
      border: "1px solid",
      borderColor: pressed ? color || BOARD_COLORS.accent : "divider",
      bgcolor: pressed ? "action.selected" : "background.paper",
      opacity: pressed ? 1 : 0.6,
      fontSize: 11.5,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </ButtonBase>
);

const Row: FC<{ label?: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 0.75 }}>
    {label ? (
      <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", width: 68 }}>
        {label}
      </Typography>
    ) : null}
    {children}
  </Box>
);

export interface FilterPanelProps {
  filters: BoardFilters;
  layers: LayerToggles;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilters: (next: Partial<BoardFilters>) => void;
  onStatusChip: (status: StatusKey) => void;
  onLayers: (next: Partial<LayerToggles>) => void;
  onReset: () => void;
  hubOptionList: MultiSelectOption[];
  hubCounts: Map<string, HubFacet>;
  clientOptionList: MultiSelectOption[];
  clientCounts: Map<string, number>;
}

/**
 * The side panel's controls. Collapsible, because on a tablet the trip list needs the room and
 * the collapsed bar can carry the one thing you need to know while it is shut: how many filters
 * are on, and therefore how much of the board you are not looking at.
 */
const FilterPanel: FC<FilterPanelProps> = ({
  filters,
  layers,
  open,
  onOpenChange,
  onFilters,
  onStatusChip,
  onLayers,
  onReset,
  hubOptionList,
  hubCounts,
  clientOptionList,
  clientCounts,
}) => {
  const active = activeFilterCount(filters, layers);

  const hubOptionsWithCounts = hubOptionList.map((o) => {
    const c = hubCounts.get(o.value);
    return { ...o, count: `${c?.own ?? 0}${c?.backhaul ? ` + ${c.backhaul} backhaul` : ""}` };
  });
  const clientOptionsWithCounts = clientOptionList.map((o) => ({
    ...o,
    count: String(clientCounts.get(o.value) ?? 0),
  }));

  return (
    <Box>
      <ButtonBase
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        aria-controls="live-board-filters"
        sx={{ width: "100%", justifyContent: "flex-start", gap: 0.5, px: 1, py: 0.75, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Filters</Typography>
        <Typography sx={{ fontSize: 11, color: active ? BOARD_COLORS.accent : "text.secondary" }}>
          {active ? `· ${active} active` : "· none active"}
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{ ml: "auto", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}
        />
      </ButtonBase>

      <Collapse in={open}>
        <Box id="live-board-filters" sx={{ p: 1 }}>
          <Button
            size="small"
            fullWidth
            variant={active ? "contained" : "outlined"}
            color={active ? "warning" : "secondary"}
            onClick={onReset}
            title="Clear every filter, layer toggle and selection; reset Upcoming to 4 h; fit the map"
            sx={{ textTransform: "none", mb: 1, fontSize: 12 }}
          >
            Show everything
            <Box component="span" sx={{ ml: 0.5, opacity: 0.75, fontWeight: 400 }}>
              · clear all filters
            </Box>
          </Button>

          <Row label="Trip status">
            {STATUS_CHIPS.map((c) => (
              <Chip
                key={c.key}
                pressed={filters.statuses.has(c.key)}
                onClick={() => onStatusChip(c.key)}
                title={c.title}
                color={LEG_COLOR[c.glyph]}
              >
                <LegGlyph leg={c.glyph} color={LEG_COLOR[c.glyph]} />
                {c.label}
              </Chip>
            ))}
          </Row>

          <Row label="Layers">
            <Chip pressed={layers.hubs} onClick={() => onLayers({ hubs: !layers.hubs })}>
              Hubs
            </Chip>
            <Chip
              pressed={layers.labels}
              onClick={() => onLayers({ labels: !layers.labels })}
              title="Unit numbers on vans, trip numbers on pending pickup points"
            >
              Unit / trip labels
            </Chip>
            <Chip
              pressed={layers.vehiclesOnly}
              onClick={() => onLayers({ vehiclesOnly: !layers.vehiclesOnly })}
              title="Show vehicles only — hides pickup/dropoff marks and the lines to them; pending trips (no vehicle yet) drop out too"
            >
              Vehicles only
            </Chip>
          </Row>

          <Row label="Other">
            <Chip
              pressed={layers.backhaulMode}
              onClick={() => onLayers({ backhaulMode: !layers.backhaulMode })}
              color={BOARD_COLORS.backhaul}
              title="Ring every trip whose dropoff is within 15 drive-minutes of another hub"
            >
              <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: BOARD_COLORS.backhaul }} />
              Highlight backhaul opportunities
            </Chip>
          </Row>

          <Row label="Upcoming">
            <TextField
              select
              size="small"
              value={filters.hours}
              onChange={(e) => onFilters({ hours: +e.target.value })}
              inputProps={{ "aria-label": "How far ahead to show not-yet-started trips" }}
              sx={{ minWidth: 150, "& .MuiInputBase-input": { fontSize: 12 } }}
            >
              {HOURS.map((h) => (
                <MenuItem key={h} value={h} sx={{ fontSize: 12 }}>
                  next {h} hours
                </MenuItem>
              ))}
            </TextField>
          </Row>

          <Row>
            <TextField
              size="small"
              fullWidth
              value={filters.query}
              onChange={(e) => onFilters({ query: e.target.value.trim().toLowerCase() })}
              placeholder="Search trip, driver, unit, train, hub, client…"
              inputProps={{ "aria-label": "Search trips" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
            />
          </Row>

          <Row>
            <MultiSelectFilter
              noun="hubs"
              options={hubOptionsWithCounts}
              selection={filters.hubs}
              onChange={(hubs) => onFilters({ hubs })}
              title="Shows the selected hubs' own trips plus trips whose dropoff is within 15 min of one of them (backhaul candidates). Counts reflect the other active filters."
            />
            <TextField
              select
              size="small"
              value={filters.sort}
              onChange={(e) => onFilters({ sort: e.target.value as SortKey })}
              inputProps={{ "aria-label": "Sort" }}
              sx={{ minWidth: 150, "& .MuiInputBase-input": { fontSize: 12 } }}
            >
              {SORTS.map((s) => (
                <MenuItem key={s.value} value={s.value} sx={{ fontSize: 12 }}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Row>

          <Row>
            <MultiSelectFilter
              noun="clients"
              options={clientOptionsWithCounts}
              selection={filters.clients}
              onChange={(clients) => onFilters({ clients })}
              hiddenByDefault={isHiddenByDefaultClient}
              title="Counts reflect the other active filters. RailCrew Xpress is internal and starts unchecked."
            />
          </Row>
        </Box>
      </Collapse>
    </Box>
  );
};

export default FilterPanel;
